// =====================================================================================
// INVRT Radar - web console - loading / empty / error states
//
// Every surface in this console renders one of these rather than a blank panel. The error
// state is the ONE place a failed request becomes words: it maps the API's stable token
// through src/lib/errorTokens.ts and shows the correlation id, so a customer reporting a
// problem hands INVRT the exact request to look up.
// =====================================================================================

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Inbox, RefreshCw } from "lucide-react";
import { copyForToken } from "@/lib/errorTokens";
import { isApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

export function LoadingState({ label = "Loading…", className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center gap-3 px-4 py-10 text-sm text-ink-muted", className)}
    >
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
        aria-hidden="true"
      />
      {label}
    </div>
  );
}

export type EmptyStateTone = "first-run" | "empty" | "filtered" | "error";

/**
 * Canonical mirror note (Story PS1): the INVRT Design System's EmptyState
 * (components/State/EmptyState.d.ts) distinguishes four tones - nothing yet (first-run),
 * nothing left (empty), nothing found (filtered), nothing loaded (error) - and renders an
 * error tone as role="alert" so it interrupts. The tone decides the ink and the
 * semantics; it never decides the copy, which stays specific to the surface. The tone is
 * additive: existing callers that pass only title/body keep the exact previous render.
 */
export function EmptyState({
  title,
  body,
  action,
  secondary,
  tone,
  compact,
  className,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
  secondary?: ReactNode;
  tone?: EmptyStateTone;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      data-tone={tone}
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        "flex flex-col items-center gap-3 rounded-card border border-dashed border-line px-6 py-12 text-center",
        compact && "py-8",
        className,
      )}
    >
      <Inbox className="h-6 w-6 text-ink-faint" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="max-w-md text-sm text-ink-muted">{body}</p>
      </div>
      {(action || secondary) && (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  /** Delivery reads cannot establish a previous mutation or money outcome. */
  deliveryRead?: boolean;
}

/** Turns any thrown value into honest, actionable copy. Never renders a raw token. */
export function ErrorState({ error, onRetry, className, deliveryRead = false }: ErrorStateProps) {
  const apiError = isApiError(error) ? error : null;
  // These delivery-only tokens describe transport truth without changing global
  // billing/refusal copy used by other product flows.
  const deliveryCopy = {
    delivery_read_timeout: { title: "Delivery setup did not answer in time", body: "This read timed out. Your draft is kept. Try reading the delivery setup again.", retryable: true },
    delivery_read_failed: { title: "Delivery setup could not be read", body: "The delivery service could not answer this read. Your draft is kept. Try again shortly.", retryable: true },
    delivery_read_invalid: { title: "Delivery setup could not be verified", body: "The delivery response could not be read safely. Your draft is kept. Try reading the delivery setup again.", retryable: true },
    delivery_save_unknown: { title: "The save outcome is unknown", body: "The service may have saved your selection. Check the saved selection before another attempt. Your draft is kept.", retryable: false },
  };
  const serviceCopy = copyForToken(apiError?.token);
  const knownReadRefusal = apiError !== null && [
    "unauthenticated", "forbidden", "validation_failed", "config_missing", "config_invalid",
    "tenant_mismatch", "selection_revision_conflict", "selection_not_configured",
  ].includes(apiError.token);
  const copy = apiError && Object.hasOwn(deliveryCopy, apiError.token)
    ? deliveryCopy[apiError.token as keyof typeof deliveryCopy]
    : deliveryRead ? {
      title: knownReadRefusal ? serviceCopy.title : deliveryCopy.delivery_read_failed.title,
      body: apiError?.token === "unauthenticated"
        ? "Sign in again to read the delivery setup. Your draft is kept. This read cannot confirm the outcome of an earlier save."
        : "The delivery service refused or could not answer this read. Your draft is kept. This read cannot confirm the outcome of an earlier save. Send INVRT the correlation id below if you need help.",
      retryable: knownReadRefusal ? serviceCopy.retryable : true,
    } : serviceCopy;
  const fieldErrors = apiError?.fieldErrors() ?? [];
  const suppressedRefs = apiError?.suppressedRefs() ?? [];
  const fallbackMessage =
    !deliveryRead && !apiError && error instanceof Error && error.message !== "" ? error.message : null;

  return (
    <div
      role="alert"
      className={cn("rounded-card border border-red-200 bg-red-50/60 p-5", className)}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-2">
          <p className="text-sm font-semibold text-ink">{copy.title}</p>
          <p className="text-sm ink-body">{copy.body}</p>

          {fallbackMessage ? (
            <p className="text-sm ink-body">{fallbackMessage}</p>
          ) : null}

          {fieldErrors.length > 0 ? (
            <ul className="list-inside list-disc text-sm ink-body">
              {fieldErrors.map((field) => (
                <li key={field.field}>
                  <span className="font-medium text-ink">{field.field}</span>: {field.message}
                </li>
              ))}
            </ul>
          ) : null}

          {suppressedRefs.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {suppressedRefs.map((ref) => (
                <li
                  key={ref}
                  className="rounded-full bg-white px-2 py-0.5 font-mono text-xs ink-body"
                >
                  {ref}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="flex flex-wrap items-center gap-3 pt-1">
            {onRetry && copy.retryable ? (
              <Button variant="secondary" size="sm" className="min-h-11 min-w-11" onClick={onRetry}>
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </Button>
            ) : null}
            {/* apiError is null for a render-time fault (ErrorBoundary's own fallback passes
                a plain Error here, with no Router in the tree at that point) - this link is
                therefore only ever reachable when isApiError(error) is true, which happens
                inside a query/mutation error, always rendered under the app's own Router. */}
            {apiError?.token === "insufficient_credits" ? (
              <Link
                to="/account?tab=credits"
                className="text-sm font-medium text-primary underline-offset-2 hover:underline"
              >
                Top up credits
              </Link>
            ) : null}
            {apiError?.correlationId ? (
              <p className="text-xs ink-body">
                Correlation id:{" "}
                <code className="font-mono ink-body">{apiError.correlationId}</code>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
